/* eslint-disable react/jsx-no-bind */
import React from 'react';
import Card from 'wonderbly-components/lib/Card';
import IconList from 'wonderbly-components/lib/IconList';
import Carousel from '../Carousel';
import Grid from './Grid';
import PropTypes from 'prop-types';
import './ProductGrid.scss';
import { prismicImage } from 'src/common/utils/prismic-image';
import scrollTo from 'src/client/js/view/utils/scrollTo';
import ProductForm from '../ProductForm';
import RichText from '../Prismic/RichText';

const imageSizeOptions = ['contain', 'cover'];
const sizeOptions = ['extra-small', 'small', 'medium', 'large'];

const ProductGridCarousel = ({
  backgroundColor,
  images,
  imageSize,
  size,
  id
}) => (
  <div
    className="product-grid__carousel"
    style={{
      backgroundColor: backgroundColor,
      height: size === 'large' ? '100%' : 0
    }}>
    <div className="product-grid__carousel-inner">
      <Carousel group={`card-carousel-${id}`}>
        {images.map((image, index) => (
          <div
            className="product-grid__carousel-item"
            key={index}
            style={{
              backgroundImage: `url(${prismicImage(image.image)})`,
              backgroundSize: imageSize
            }}
            draggable="true"
          />
        ))}
      </Carousel>
    </div>
  </div>
);

ProductGridCarousel.propTypes = {
  backgroundColor: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  images: PropTypes.arrayOf(PropTypes.shape({ image: PropTypes.any })),
  imageSize: PropTypes.oneOf(imageSizeOptions).isRequired,
  size: PropTypes.oneOf(sizeOptions).isRequired
};

const ProductGrid = props => {
  let largeCardCount = 0;

  return (
    <section className="product-grid">
      <div className="container-guttered">
        {props.title && (
          <div className="product-grid__header">
            <h4 className="product-grid__title text-mega">{props.title}</h4>

            {props.description && (
              <div className="product-grid__description">
                <RichText fields={props.description} />
              </div>
            )}
          </div>
        )}
        <Grid>
          {props.items.map(
            (
              {
                product,
                badgeOverride,
                colourOverride,
                imageSize,
                largeCardImageSide,
                largeCardPoint1,
                largeCardPoint1Icon,
                largeCardPoint2,
                largeCardPoint2Icon,
                price,
                savings,
                size,
                url,
                useCarousel,
                forceOpenForm = false,
                extraBottomSpacing = false,
                ctaBehaviour,
              },
              itemIndex
            ) => {
              const {
                productInformation: {
                  backgroundColor,
                  description,
                  longDescriptionMd,
                  title,
                  image,
                  minimumAge,
                  maximumAge,
                  productBadgeContent,
                  productBadgeColour,
                  carouselImages
                },
                main: { productId }
              } = product;

              let width = 12;
              let tabletWidth = 12;

              if (size === 'medium') {
                width = 6;
                tabletWidth = 6;
              } else if (size === 'small') {
                width = 4;
                tabletWidth = 6;
              } else if (size === 'extra-small') {
                width = 3;
                tabletWidth = 6;
              }

              if (size === 'large') {
                largeCardCount += 1;
              }

              let largeImageSide, cta;

              if (largeCardImageSide === 'auto') {
                // Alternate left and right in same grid
                largeImageSide = largeCardCount % 2 === 1 ? 'right' : 'left';
              } else {
                // Use user pref if explicitly set
                largeImageSide = largeCardImageSide;
              }

              const shouldShowCreatorForm =
                ctaBehaviour === 'show-creator-form';
              const shouldForceFormOpen =
                forceOpenForm && shouldShowCreatorForm;

              const isExpanded =
                props.expandedId === productId || shouldForceFormOpen;

              const renderImage = () => (
                <ProductGridCarousel
                  backgroundColor={backgroundColor}
                  images={carouselImages}
                  imageSize={imageSize}
                  size={size}
                  id={itemIndex}
                />
              );

              const renderAppendix = () => (
                <div id={`product-grid__card-appendix-${productId}`}>
                  {isExpanded && (
                    <ProductForm primary={{ product }} id={productId} />
                  )}
                </div>
              );

              if (!shouldForceFormOpen) {
                cta = isExpanded ? 'Close the form' : 'Create yours now!';
              }

              return (
                <Grid.Item
                  width={width}
                  tabletWidth={tabletWidth}
                  key={itemIndex}
                  extraPadding={
                    extraBottomSpacing && size === 'large'
                  }>
                  <Card
                    ageMax={parseInt(maximumAge, 10)}
                    ageMin={parseInt(minimumAge, 10)}
                    badgeText={badgeOverride || productBadgeContent}
                    badgeInline={size === 'medium'}
                    body={
                      size === 'large' && longDescriptionMd ? (
                        <RichText fields={longDescriptionMd} />
                      ) : (
                        description
                      )
                    }
                    colour={colourOverride || productBadgeColour || 'green'}
                    cta={cta}
                    image={image.url}
                    imageBackgroundColour={backgroundColor}
                    imageBackgroundSize={imageSize}
                    large={size === 'large'}
                    largeImageSide={largeImageSide}
                    fadeButton={isExpanded}
                    onClick={
                      shouldShowCreatorForm
                        ? () => {
                          props.setExpandedId(isExpanded ? null : productId);

                          if (!isExpanded && props.media.fabletDown) {
                            scrollTo(
                              `#product-grid__card-appendix-${productId}`,
                              {
                                offset: 24,
                                collapsibleSelector:
                                  props.expandedId &&
                                  `#product-grid__card-appendix-${props.expandedId}`
                              }
                            );
                          }
                        }
                        : undefined
                    }
                    points={[
                      largeCardPoint1 && (
                        <IconList.Item icon={largeCardPoint1Icon} key="1">
                          {largeCardPoint1}
                        </IconList.Item>
                      ),
                      largeCardPoint2 && (
                        <IconList.Item icon={largeCardPoint2Icon} key="2">
                          {largeCardPoint2}
                        </IconList.Item>
                      ),
                    ]}
                    price={`From ${price.formatted}`}
                    priceOriginal={savings && savings.was_price.formatted}
                    renderImage={
                      useCarousel && renderImage
                    }
                    renderAppendix={renderAppendix}
                    title={title}
                    href={
                      !shouldForceFormOpen || !shouldShowCreatorForm
                        ? url
                        : undefined
                    }
                    onlyLinkInner={useCarousel}
                  />
                </Grid.Item>
              );
            }
          )}
        </Grid>
      </div>
    </section>
  );
};

ProductGrid.propTypes = {
  title: PropTypes.string,
  description: PropTypes.array,
  expandedId: PropTypes.string,
  setExpandedId: PropTypes.func,
  fetchPrismicProduct: PropTypes.func,
  media: PropTypes.shape({ fabletDown: PropTypes.bool }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      badgeOverride: PropTypes.string,
      ctaBehaviour: PropTypes.oneOf(['show-creator-form', 'open-product-page']),
      colourOverride: PropTypes.string,
      extraBottomSpacing: PropTypes.bool,
      forceOpenForm: PropTypes.bool,
      imageSize: PropTypes.oneOf(imageSizeOptions).isRequired,
      imageSize: PropTypes.string,
      largeCardImageSide: PropTypes.oneOf(['auto', 'left', 'right']).isRequired,
      largeCardPoint1: PropTypes.string,
      largeCardPoint1Icon: PropTypes.string,
      largeCardPoint2: PropTypes.string,
      largeCardPoint2Icon: PropTypes.string,
      price: PropTypes.shape({
        amount: PropTypes.string.isRequired,
        formatted: PropTypes.string.isRequired
      }).isRequired,
      product: PropTypes.any,
      savings: PropTypes.shape({
        was_price: PropTypes.shape({
          amount: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired
        }).isRequired
      }),
      size: PropTypes.oneOf(sizeOptions).isRequired,
      useCarousel: PropTypes.bool,
    })
  ).isRequired
};

export default ProductGrid;



// WEBPACK FOOTER //
// ./src/client/js/view/components/ProductGrid/ProductGrid.js